import { createContext, useContext, type ReactNode } from 'react';

/** The few labels the kit renders on its own (close buttons, the command palette chrome). Pages pass everything else. */
export type KitStrings = {
  close: string;
  dismiss: string;
  loading: string;
  notifications: string;
  commandPlaceholder: string;
  commandLabel: string;
  commandEmpty: string;
  commandNavigate: string;
  commandSelect: string;
  commandClose: string;
  sortAscending: string;
  sortDescending: string;
};

export const KIT_STRINGS: Record<'en' | 'ar', KitStrings> = {
  en: {
    close: 'Close',
    dismiss: 'Dismiss',
    loading: 'Loading',
    notifications: 'Notifications',
    commandPlaceholder: 'Search pages and actions…',
    commandLabel: 'Command menu',
    commandEmpty: 'No results. Try another word.',
    commandNavigate: 'to move',
    commandSelect: 'to open',
    commandClose: 'to close',
    sortAscending: 'sorted ascending',
    sortDescending: 'sorted descending',
  },
  ar: {
    close: 'إغلاق',
    dismiss: 'تجاهل',
    loading: 'جارٍ التحميل',
    notifications: 'الإشعارات',
    commandPlaceholder: 'ابحث في الصفحات والإجراءات…',
    commandLabel: 'قائمة الأوامر',
    commandEmpty: 'لا توجد نتائج. جرّب كلمة أخرى.',
    commandNavigate: 'للتنقل',
    commandSelect: 'للفتح',
    commandClose: 'للإغلاق',
    sortAscending: 'مرتّب تصاعديًا',
    sortDescending: 'مرتّب تنازليًا',
  },
};

type KitContext = { locale: 'en' | 'ar'; dir: 'ltr' | 'rtl'; strings: KitStrings };
const Ctx = createContext<KitContext>({ locale: 'en', dir: 'ltr', strings: KIT_STRINGS.en });

/** Gives kit components their locale, direction and built-in labels. Wrap each surface once. */
export function KitProvider({ locale = 'en', strings, children }: { locale?: 'en' | 'ar'; strings?: Partial<KitStrings>; children: ReactNode }) {
  const value: KitContext = { locale, dir: locale === 'ar' ? 'rtl' : 'ltr', strings: { ...KIT_STRINGS[locale], ...strings } };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useKit = () => useContext(Ctx);
